import React, {
    FC,
    FormEvent,
    SyntheticEvent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { FormikValues } from 'formik';
import AutoCompleteInput from '../inputs/autoCompleteInput';
import useGetCustomFields from '../../apiHooks/queries/getCustomFields/useGetCustomFields';
import { useCustomFieldsStore } from '../../store/customFieldsStore';

export interface CustomFieldInterface {
    id: string;
    name: string;
}

interface CustomFieldsSelectInterface {
    data?: CustomFieldInterface | null;
    formik?: FormikValues;
    fullWidth?: boolean;
    size?: 'small' | 'medium';
    setSelectedCustomFieldId?: (_id: string) => void;
    setSelectedCustomField?: (_field: CustomFieldInterface | null) => void;
    disabled?: boolean;
    label: string;
    placeholder: string;
    identifier: string;
}

type Step = 'FIELD' | 'VALUE';

const CustomFieldsSelect: FC<CustomFieldsSelectInterface> = (props) => {
    const [step, setStep] = useState<Step>('FIELD');
    const [searchedValue, setSearchedValue] = useState('');
    const [selectedField, setSelectedField] = useState<CustomFieldInterface | null>(null);
    const [selectedValue, setSelectedValue] = useState<CustomFieldInterface | null>(null);

    const { data: customFieldsData, isFetching } =
        useGetCustomFields(props.identifier);

    const { setSelectedCustomField, addCustomField } = useCustomFieldsStore();

    const fields = customFieldsData?.data?.data?.fields || {};
    const values = customFieldsData?.data?.data?.values || {};

    const fieldOptions: CustomFieldInterface[] = useMemo(() => {
        if (!fields || !values) return [];

        return Object.entries(fields)
            .filter(([fieldId]) => Array.isArray(values[fieldId]) && values[fieldId].length > 0)
            .map(([id, name]) => ({
                id: String(id),
                name: String(name),
            }));
    }, [fields, values]);

    const valueOptions: CustomFieldInterface[] = useMemo(() => {
        if (!selectedField) return [];

        const fieldValues = values[selectedField.id];

        if (!Array.isArray(fieldValues)) return [];

        return fieldValues.map((value: string) => ({
            id: `${selectedField.id}:${value}`,
            name: value,
        }));
    }, [values, selectedField]);


    const options = step === 'FIELD' ? fieldOptions : valueOptions;

    const handleChange = (
        _: FormEvent,
        newValue: CustomFieldInterface | null
    ) => {
        if (!newValue) {
            setStep('FIELD');
            setSelectedField(null);
            setSelectedValue(null);
            setSearchedValue('');

            props.formik?.setFieldValue('customFieldId', '');
            props.formik?.setFieldValue('customFieldValue', '');

            setSelectedCustomField(null);
            return;
        }

        if (step === 'FIELD') {
            setSelectedField(newValue);
            setStep('VALUE');
            setSearchedValue('');
            return;
        }

        if (step === 'VALUE' && selectedField) {
            setSelectedValue(newValue);

            const payload = {
                id: selectedField.id,
                name: selectedField.name,
                value: newValue.name,
            };

            props.formik?.setFieldValue('customFieldId', selectedField.id);
            props.formik?.setFieldValue('customFieldValue', newValue.name);

            props.setSelectedCustomFieldId?.(selectedField.id);
            props.setSelectedCustomField?.({
                id: selectedField.id,
                name: selectedField.name,
            });

            setSelectedCustomField({
                id: selectedField.id,
                name: selectedField.name,
            });

            addCustomField(payload as any);
        }
    };

    const handleInputChange = (
        _: SyntheticEvent,
        value: string
    ) => {
        setSearchedValue(value);
    };

    if (!Object.keys(fields).length) return null;

    return (
        <AutoCompleteInput
            key="customFieldSelect"
            name="customFieldSelect"
            label={props.label}
            placeholder={
                step === 'FIELD'
                    ? props.placeholder
                    : `Select value for "${selectedField?.name}"`
            }
            size={props.size}
            style={{ minWidth: 250 }}
            options={options}
            getOptionLabel={(option: CustomFieldInterface) => option?.name || ''}
            value={step === 'FIELD' ? selectedField : selectedValue}
            loading={isFetching}
            onChange={handleChange}
            inputValue={
                step === 'FIELD'
                    ? selectedField?.name || searchedValue
                    : selectedValue?.name || searchedValue
            }
            onInputChange={handleInputChange}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth={props.fullWidth}
            disabled={props.disabled}
        />
    );
};

export default CustomFieldsSelect;
